import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { MaintenancePage } from '@/pages/MaintenancePage';
import { HomePage } from '@/pages/HomePage';
import { SearchPage } from '@/pages/SearchPage';
import { CustomerJobsPage } from '@/pages/CustomerJobsPage';
import { ProviderJobsPage } from '@/pages/ProviderJobsPage';
import { JobDetailPage } from '@/pages/JobDetailPage';
import { CreateJobPage } from '@/pages/CreateJobPage';
import { CustomerProfilePage } from '@/pages/CustomerProfilePage';
import { IndividualProviderProfilePage } from '@/pages/IndividualProviderProfilePage';
import { CompanyProviderProfilePage } from '@/pages/CompanyProviderProfilePage';
import { ProviderProfileWizard } from '@/pages/ProviderProfileWizard';
import { IndividualProfileWizard } from '@/pages/IndividualProfileWizard';
import { CompanyProfileWizard } from '@/pages/CompanyProfileWizard';
import { WizardRouter } from '@/pages/WizardRouter';
import { ProviderProfilePage } from '@/pages/ProviderProfilePage';
import { CustomerSettingsPage } from '@/pages/CustomerSettingsPage';
import { ProviderSettingsPage } from '@/pages/ProviderSettingsPage';
import { ProvidersPage } from '@/pages/ProvidersPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ProviderDashboardPage } from '@/pages/ProviderDashboardPage';
import { CompanyDashboardPage } from '@/pages/CompanyDashboardPage';
import { ChatPage } from '@/pages/ChatPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { PremiumPage } from '@/pages/PremiumPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { TeamPage } from '@/pages/TeamPage';
import { ReviewsPage } from '@/pages/ReviewsPage';
import { useBrand } from '@/hooks/useBrand';
import { InvoicesPage } from '@/pages/InvoicesPage';
import { CreateInvoicePage } from '@/pages/CreateInvoicePage';
import { ProviderMyJobsPage } from '@/pages/provider/ProviderMyJobsPage';
import { ProviderQuotesPage } from '@/pages/provider/ProviderQuotesPage';
import { QuoteTemplatesPage } from '@/pages/QuoteTemplatesPage';
import { AdminDashboardPage } from '@/pages/AdminDashboardPage';
import { WebhookManagementPage } from '@/pages/WebhookManagementPage';
import { SecurityPage } from '@/pages/SecurityPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { TermsPage } from '@/pages/TermsPage';
import { CompanyInvitationsPage } from '@/pages/CompanyInvitationsPage';
import { ReceivedInvitationsPage } from '@/pages/ReceivedInvitationsPage';
import { CookieConsentBanner } from '@/components/gdpr/CookieConsentBanner';
import { Footer } from '@/components/layout/Footer';

import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { TestingPage } from '@/pages/TestingPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { OfflineIndicator } from '@/components/ui/OfflineIndicator';
import { EnvDebugger } from '@/components/debug/EnvDebugger';
import { AuthDebug } from '@/components/debug/AuthDebug';
import { ToastProvider } from '@/contexts/ToastContext';
import { pushNotificationService } from '@/services/pushNotificationService';
import { useAuthStore } from '@/store/authStore';
import { useSettingsStore } from '@/store/settingsStore';
import '@/utils/envChecker'; // Import environment checker
import './App.css';
import './styles/design-system.css';
import './styles/animations.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  const { user, initialize } = useAuthStore();
  const brand = useBrand();

  // Set dynamic document title
  useEffect(() => {
    document.title = `${brand.name} - Service Marketplace`;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `${brand.name} - ${brand.metaDescription}`);
    }
  }, [brand.name, brand.metaDescription]);

  // Check if maintenance mode is enabled
  const maintenanceEnv = import.meta.env.VITE_MAINTENANCE_MODE;
  const isMaintenanceMode = maintenanceEnv === 'true';

  // If maintenance mode is enabled, show maintenance page
  if (isMaintenanceMode) {
    return (
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Routes>
                <Route path="*" element={<MaintenancePage />} />
              </Routes>
            </Router>
          </ToastProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    );
  }

  useEffect(() => {
    // Initialize auth state only on client side to prevent hydration issues
    if (typeof window !== 'undefined') {
      initialize();
    }
  }, [initialize]);

  useEffect(() => {
    // Initialize push notifications when user logs in
    if (user) {
      initializePushNotifications();
    }
  }, [user?.id]); // Only depend on user ID to prevent re-initialization

  const initializePushNotifications = async () => {
    try {
      const initialized = await pushNotificationService.initialize();
      if (initialized && user) {
        await pushNotificationService.subscribe(user.id);
        console.log('Push notifications initialized for user:', user.id);
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
    }
  };

  const { utilityMode, highContrast } = useSettingsStore();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className={`min-h-screen transition-colors duration-300 ${utilityMode ? 'utility-mode' : ''} ${highContrast ? 'high-contrast' : ''} ${utilityMode ? 'bg-white' : 'bg-gray-50'}`}>
              <OfflineIndicator />
              <Routes>
                {/* Public routes */}
                <Route path="login" element={<LoginPage />} />
                <Route path="signup" element={<SignupPage />} />
                <Route path="forgot-password" element={<ForgotPasswordPage />} />
                <Route path="auth/callback" element={<AuthCallbackPage />} />

                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="customer-jobs" element={
                    <ProtectedRoute>
                      <CustomerJobsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="provider-jobs" element={
                    <ProtectedRoute>
                      <ProviderJobsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="provider">
                    <Route path="my-jobs" element={
                      <ProtectedRoute>
                        <ProviderMyJobsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="quotes" element={
                      <ProtectedRoute>
                        <ProviderQuotesPage />
                      </ProtectedRoute>
                    } />
                  </Route>
                  <Route path="jobs" element={
                    <ProtectedRoute>
                      <ProviderJobsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="jobs/:id" element={<JobDetailPage />} />
                  <Route path="providers" element={<ProvidersPage />} />
                  {/* Protected routes */}
                  <Route path="dashboard" element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="provider/dashboard" element={
                    <ProtectedRoute>
                      <ProviderDashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="company/dashboard" element={
                    <ProtectedRoute>
                      <CompanyDashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="create-job" element={<CreateJobPage />} />
                  <Route path="profile/customer" element={
                    <ProtectedRoute>
                      <CustomerProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="profile/provider" element={
                    <ProtectedRoute>
                      <ProviderProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="profile/wizard" element={
                    <ProtectedRoute>
                      <WizardRouter />
                    </ProtectedRoute>
                  } />
                  <Route path="profile/wizard/individual" element={
                    <ProtectedRoute>
                      <IndividualProfileWizard />
                    </ProtectedRoute>
                  } />
                  <Route path="profile/wizard/company" element={
                    <ProtectedRoute>
                      <CompanyProfileWizard />
                    </ProtectedRoute>
                  } />
                  <Route path="profile/individual" element={
                    <ProtectedRoute>
                      <IndividualProviderProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="profile/company" element={
                    <ProtectedRoute>
                      <CompanyProviderProfilePage />
                    </ProtectedRoute>
                  } />
                  <Route path="settings" element={
                    <ProtectedRoute>
                      <CustomerSettingsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/provider" element={
                    <ProtectedRoute>
                      <ProviderSettingsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="chat" element={
                    <ProtectedRoute>
                      <ChatPage />
                    </ProtectedRoute>
                  } />
                  <Route path="chat/:id" element={
                    <ProtectedRoute>
                      <ChatPage />
                    </ProtectedRoute>
                  } />
                  <Route path="premium" element={
                    <ProtectedRoute>
                      <PremiumPage />
                    </ProtectedRoute>
                  } />
                  <Route path="calendar" element={
                    <ProtectedRoute>
                      <CalendarPage />
                    </ProtectedRoute>
                  } />
                  <Route path="analytics" element={
                    <ProtectedRoute>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="team" element={
                    <ProtectedRoute>
                      <TeamPage />
                    </ProtectedRoute>
                  } />
                  <Route path="company/invitations" element={
                    <ProtectedRoute>
                      <CompanyInvitationsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="invitations" element={
                    <ProtectedRoute>
                      <ReceivedInvitationsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="reviews" element={
                    <ProtectedRoute>
                      <ReviewsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="invoices" element={
                    <ProtectedRoute>
                      <InvoicesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="invoices/create" element={
                    <ProtectedRoute>
                      <CreateInvoicePage />
                    </ProtectedRoute>
                  } />
                  <Route path="quote-templates" element={
                    <ProtectedRoute>
                      <QuoteTemplatesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="security" element={
                    <ProtectedRoute>
                      <SecurityPage />
                    </ProtectedRoute>
                  } />
                  <Route path="privacy" element={
                    <ProtectedRoute>
                      <PrivacyPage />
                    </ProtectedRoute>
                  } />
                  <Route path="admin" element={
                    <ProtectedRoute>
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  } />
                  <Route path="admin/webhooks" element={
                    <ProtectedRoute>
                      <WebhookManagementPage />
                    </ProtectedRoute>
                  } />

                  {/* Development Testing Route - Only available in dev mode */}
                  <Route path="testing" element={<TestingPage />} />

                </Route>

                {/* Public Legal Pages */}
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
              </Routes>
              <Footer />
              <CookieConsentBanner />
              <EnvDebugger />
              <AuthDebug />
            </div>
          </Router>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;